import * as React from "react";
import Immutable from "immutable";
import { useRef, useMemo } from "react";
import styled, { css } from "styled-components";
import WidgetRenderingContext from "views/components/widgets/WidgetRenderingContext";
import ColorMapper from "views/components/visualizations/ColorMapper";
import Icon from "components/common/Icon";
import ChartColorContext from "views/components/visualizations/ChartColorContext";
import PluggableStoreProvider from "views/stores/ViewsStoreProvider";
import View from "views/logic/views/View";
import SearchExecutionState from "views/logic/search/SearchExecutionState";
import ViewState from "views/logic/views/ViewState";
import Query from "views/logic/queries/Query";
import Search from "views/logic/search/Search";
import Caption from "common/components/widgetRenderers/Caption";
import WidgetDescription, { FallbackWidgetDescription } from "common/components/widgetRenderers/WidgetDescription";
import AutoRefreshDisabledProvider from "report/common/AutoRefreshDisabledProvider";
import { Spinner } from "components/common";
import PrintingWidgetVisualizationContainer from "report/common/PrintingWidgetVisualizationContainer";
import Heading from "common/components/widgetRenderers/Heading";
import ErrorBoundary from "components/errors/ErrorBoundary";
const MARGIN_HEIGHT = 10;
export const Container = styled.div`
  font-size: 90%;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;

  h3 {
    color: black;
  }

  @media print {
    padding: 0;
    page-break-inside: avoid;
  }
`;
const DragHandleIcon = styled(Icon)`
  font-size: 1.2em;
  position: absolute;
  left: 20px;
  top: 25px;

  @media print {
    display: none;
  }
`;
const executionState = SearchExecutionState.empty();
const view = View.builder().state({ query: ViewState.create() }).search(
  Search.builder().queries([Query.builder().id("query").build()]).build()
).build();
const SpinnerContainer = styled.div(
  ({ $height }) => css`
    width: 100%;
    height: ${$height != null ? $height : 300}px;
    display: flex;
    align-items: center;
    justify-content: center;
  `
);
const widgetRendering = { limitHeight: false };
const PrintWidget = ({
  description = void 0,
  title = void 0,
  height = void 0,
  hideDescription,
  hideQuery,
  headerId = void 0,
  interactive = true,
  limitHeight = false,
  showCaption = true,
  showHandle = true,
  showHeading = true,
  value,
  widget,
  widgetId,
  width = void 0
}) => {
  var _a, _b, _c, _d, _e, _f;
  const heading = useRef(null);
  const caption = useRef(null);
  const Description = !hideDescription ? (description == null ? void 0 : description.trim()) ? /* @__PURE__ */ React.createElement("span", null, description) : /* @__PURE__ */ React.createElement(ErrorBoundary, { FallbackComponent: FallbackWidgetDescription }, /* @__PURE__ */ React.createElement(
    WidgetDescription,
    {
      calculatedAt: value == null ? void 0 : value.calculatedAt,
      widgetConfig: widget.config,
      widgetTimerange: widget.timerange,
      widgetQuery: widget.query,
      widgetType: widget.type,
      widgetStreams: widget.streams,
      widgetFilters: widget.filters,
      hideQuery
    }
  )) : null;
  const chartColors = useMemo(
    () => {
      var _a2, _b2, _c2;
      return (_c2 = (_b2 = (_a2 = widget == null ? void 0 : widget.config) == null ? void 0 : _a2.formattingSettings) == null ? void 0 : _b2.chartColors) != null ? _c2 : {};
    },
    [(_b = (_a = widget == null ? void 0 : widget.config) == null ? void 0 : _a.formattingSettings) == null ? void 0 : _b.chartColors]
  );
  const colorMap = useMemo(() => ColorMapper.create(Immutable.Map(chartColors)), [chartColors]);
  const chartColorContext = useMemo(() => ({ colors: colorMap, setColor: () => Promise.resolve() }), [colorMap]);
  const visualizationHeight = height - ((_d = (_c = caption.current) == null ? void 0 : _c.scrollHeight) != null ? _d : 0) - ((_f = (_e = heading.current) == null ? void 0 : _e.scrollHeight) != null ? _f : 0) - MARGIN_HEIGHT;
  return /* @__PURE__ */ React.createElement(PluggableStoreProvider, { view, isNew: false, executionState, initialQuery: "query" }, /* @__PURE__ */ React.createElement(AutoRefreshDisabledProvider, null, /* @__PURE__ */ React.createElement(WidgetRenderingContext.Provider, { value: widgetRendering }, /* @__PURE__ */ React.createElement(ChartColorContext.Provider, { value: chartColorContext }, /* @__PURE__ */ React.createElement(Container, null, showHandle && /* @__PURE__ */ React.createElement(DragHandleIcon, { name: "sort" }), showHeading && /* @__PURE__ */ React.createElement(Heading, { title, ref: heading, headerId }), !value ? /* @__PURE__ */ React.createElement(SpinnerContainer, { $height: visualizationHeight }, /* @__PURE__ */ React.createElement(Spinner, null)) : /* @__PURE__ */ React.createElement(
    PrintingWidgetVisualizationContainer,
    {
      widget,
      interactive,
      limitHeight,
      visualizationHeight,
      width,
      value,
      widgetId
    }
  ), showCaption && !!Description && /* @__PURE__ */ React.createElement(Caption, { text: Description, ref: caption }))))));
};
export default PrintWidget;
